// Журнал списаний: периоды и причины. SMY-58 · P5 Э5.1
//
// Главное правило продукта, из которого следует остальное:
//
//   Единица списывается за ЗАВЕРШЁННЫЙ РЕЗУЛЬТАТ, а не за внутренний шаг,
//   вызов модели или уточняющий вопрос системы.
//
// Здесь только правила, общие клиенту и серверу. Сама запись — в
// apps/api/src/billing.

import type { UnitId } from './units.ts'

/**
 * Откуда взялась запись в журнале.
 *
 * Различаются не ради полноты: экран 10 обещает историю, в которой понятно
 * ЛЮБОЕ списание, а начисление администратором обязано быть подписано честно
 * и не выдаваться за обычный лимит (SMY-64).
 */
export const LEDGER_REASONS = ['run', 'refund', 'pack', 'grant'] as const
export type LedgerReason = (typeof LEDGER_REASONS)[number]

export const REASON_LABELS: Record<LedgerReason, string> = {
  run: 'Списано за результат',
  refund: 'Возврат',
  pack: 'Пакет докупки',
  grant: 'Начислено поддержкой',
}

/**
 * Часовой пояс сброса лимитов — московский.
 *
 * Экран 10 обещает «Продлится 3 октября. Лимиты обнулятся тогда же». Считать
 * эту дату по UTC значит обнулять лимиты в 03:00 по Москве: человек, зашедший
 * ночью третьего, увидит вчерашний остаток и напишет в поддержку. Продукт
 * российский, и одна оговорка про часовой пояс честнее, чем три часа
 * необъяснимого поведения раз в месяц.
 */
export const RESET_OFFSET_MS = 3 * 60 * 60 * 1000

/** Смещённая дата: UTC-методы над ней читают московское время. */
const inMoscow = (d: Date): Date => new Date(d.getTime() + RESET_OFFSET_MS)

/** День месяца, на который приходится граница периода, с поправкой на короткий месяц. */
const boundaryDay = (anchorDay: number, year: number, month: number): number =>
  Math.min(anchorDay, new Date(Date.UTC(year, month + 1, 0)).getUTCDate())

const boundary = (anchorDay: number, year: number, month: number): number =>
  Date.UTC(year, month, boundaryDay(anchorDay, year, month))

/**
 * Начало расчётного периода, в котором лежит `at`.
 *
 * Период считается от ДАТЫ ПОДКЛЮЧЕНИЯ ТАРИФА, а не от первого числа месяца.
 * Так обещано на экране 10: «Продлится 3 октября. Лимиты обнулятся тогда же».
 * Календарный месяц дал бы человеку, оплатившему 28-го, три дня полного
 * тарифа вместо тридцати — и это было бы не мелочью, а обманом на 90%.
 *
 * Короткий месяц: подключение 31-го числа даёт границу 28 февраля. Округление
 * вниз, а не вперёд на 1 марта: период не должен становиться длиннее месяца,
 * иначе лимит выдаётся дважды за одни деньги.
 */
export function periodStart(anchor: Date, at: Date): Date {
  const day = inMoscow(anchor).getUTCDate()
  const now = inMoscow(at)
  let year = now.getUTCFullYear()
  let month = now.getUTCMonth()

  if (boundary(day, year, month) > now.getTime()) {
    month -= 1
    if (month < 0) {
      month = 11
      year -= 1
    }
  }
  return new Date(boundary(day, year, month) - RESET_OFFSET_MS)
}

/** Когда обнулятся лимиты. Экран 10 показывает эту дату прямо. */
export function periodEnd(anchor: Date, at: Date): Date {
  const day = inMoscow(anchor).getUTCDate()
  const start = inMoscow(periodStart(anchor, at))
  const year = start.getUTCFullYear()
  const month = start.getUTCMonth()
  return new Date(
    (month === 11 ? boundary(day, year + 1, 0) : boundary(day, year, month + 1)) - RESET_OFFSET_MS,
  )
}

// --- Пакеты докупки. SMY-64 · P5 Э5.3 ---------------------------------------

/**
 * Пакет — докупка в момент нужды: лимит кончился, а задача осталась.
 *
 * СРОКА У ПАКЕТА БОЛЬШЕ НЕТ (06.09.2026). Прежде было «не сгорает до конца
 * следующего месяца»; теперь остаток копится и переносится сколько угодно
 * долго. Это ровно та разница, ради которой пакет и покупают в конце месяца:
 * купленное — купленное, а не аренда до ближайшей границы периода.
 *
 * Поле срока осталось необязательным, а не исчезло: сгорающий пакет ещё
 * может понадобиться — акция, компенсация, начисление поддержкой на неделю, —
 * и механика для него уже написана. Пусто — не сгорает.
 */
export interface PackOffer {
  unit: UnitId
  /** Сколько единиц добавляет. */
  amount: number
  /** Рубли. */
  price: number
  /** Сколько живёт после покупки. Пусто — не сгорает никогда. */
  lifetimeMonths?: number | undefined
}

/**
 * Когда сгорит пакет, купленный в этот момент. Пусто — не сгорает.
 *
 * Срок, если он задан, считается до конца КАЛЕНДАРНОГО месяца, а не через
 * тридцать дней от покупки: обещание такого рода человек проверяет по
 * календарю, и считать его от даты покупки значило бы обещать одно, а давать
 * другое.
 */
export function packExpiry(purchasedAt: Date, lifetimeMonths?: number | null): Date | null {
  if (lifetimeMonths == null) return null
  const at = new Date(purchasedAt.getTime() + RESET_OFFSET_MS)
  // Нулевой день следующего месяца — последний день текущего.
  const end = Date.UTC(at.getUTCFullYear(), at.getUTCMonth() + lifetimeMonths + 1, 0, 23, 59, 59, 999)
  return new Date(end - RESET_OFFSET_MS)
}

/**
 * Порядок расходования: СНАЧАЛА МЕСЯЧНЫЙ ЛИМИТ, ПОТОМ ПАКЕТ.
 *
 * Обратный порядок молча съедал бы купленное там, где хватило бы бесплатного,
 * и разница обнаружилась бы в конце месяца — когда пакет уже потрачен, а
 * лимит сгорел неиспользованным.
 *
 * Между пакетами — сначала СГОРАЮЩИЕ, от ближайшего срока, потом бессрочные
 * от самого старого. Причина та же, что и у главного правила: тратить надо
 * то, что можно потерять. Пока все три пакета каталога бессрочные, работает
 * вторая половина правила; первая нужна начислениям со сроком и остаётся
 * рабочей, а не декоративной.
 */
export function pickSource<T extends { remaining: number; expiresAt: Date | null; createdAt?: Date }>(
  monthlyRemaining: number,
  packs: readonly T[],
): { from: 'monthly' } | { from: 'pack'; pack: T } | null {
  if (monthlyRemaining > 0) return { from: 'monthly' }
  const pack = [...packs]
    .filter((p) => p.remaining > 0)
    .sort((a, b) => {
      if (a.expiresAt != null && b.expiresAt != null) return a.expiresAt.getTime() - b.expiresAt.getTime()
      if (a.expiresAt != null) return -1
      if (b.expiresAt != null) return 1
      return (a.createdAt?.getTime() ?? 0) - (b.createdAt?.getTime() ?? 0)
    })[0]
  return pack != null ? { from: 'pack', pack } : null
}

/**
 * Что произошло с единицей. Одна запись журнала глазами пользователя.
 *
 * `remaining` — остаток ПОСЛЕ этой записи. Он не хранится, а считается при
 * чтении: остаток рядом с историей — это второй источник истины для денег,
 * и рано или поздно он разойдётся с первым.
 */
export interface LedgerEvent {
  id: string
  unit: UnitId
  /** Отрицательное — списание, положительное — начисление. */
  delta: number
  reason: LedgerReason
  at: string
  remaining: number
  /** Запуск, за который списано. Пусто у начислений. */
  runId?: string
  /** Сценарий запуска — чтобы человек узнал в истории свою задачу. */
  scenario?: string
  /** Списано из пакета, а не из месячного лимита. Экран 10 это показывает. */
  fromPack?: boolean
}
